import React, { useEffect } from "react";
import { HiChevronRight } from "react-icons/hi";
import { HiUserGroup } from "react-icons/hi";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectGeneral } from "../../redux/slices/generalSlice";
import { useGetFeaturedCommunitiesQuery } from "../../redux/slices/api/communityApiSlice";
import { useLazyGetUserQuery } from "../../redux/slices/api/userApi";
import { Bars } from "react-loader-spinner";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper";
import CommunityCard from "../communities/CommunityCard";

const HomeCommunities = () => {
  const { data: communitiesData, isLoading } = useGetFeaturedCommunitiesQuery();
  
  // Get user state
  const { isLoggedIn } = useSelector(selectGeneral);
  const [triggerGetUser, { data: userData }] = useLazyGetUserQuery();

  useEffect(() => {
    if (isLoggedIn) {
      triggerGetUser();
    }
  }, [isLoggedIn, triggerGetUser]);

  // Check if user can create communities
  const userRole = userData?.user?.role;
  const canCreateCommunity = isLoggedIn && userRole && ['applicant', 'organization'].includes(userRole);
  const hasCompletedProfile = isLoggedIn && userRole && userRole !== 'un_registered';

  return (
    <div className="w-full flex flex-col">
      {/* Section Header */}
      <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
        <div className="flex flex-col">
          <span className="text-primary-green roboto-font text-sm font-medium uppercase tracking-wider mb-3">
            Join & Support
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl abel-font text-white">
            Web3 Communities
          </h2>
        </div>
        <Link
          to="/communities"
          className="group flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-primary/20 to-primary-green/20 hover:from-primary/30 hover:to-primary-green/30 border border-white/20 transition-all duration-300"
        >
          <span className="text-white font-medium">Explore all communities</span>
          <HiChevronRight
            className="text-white group-hover:translate-x-1 transition-transform duration-300"
            size={20}
          />
        </Link>
      </div>

      {isLoading ? (
        <div className="w-full min-h-[400px] flex justify-center items-center">
          <div className="flex flex-col items-center gap-4">
            <Bars
              height="50"
              width="50"
              color="#7e7cff"
              ariaLabel="bars-loading"
              visible={true}
            />
            <span className="text-white/60 roboto-font">Loading communities...</span>
          </div>
        </div>
      ) : communitiesData?.length > 0 ? (
        <div className="w-full min-h-[580px] px-2 sm:px-4">
          <Swiper
            pagination={{
              clickable: true,
              dynamicBullets: true,
              dynamicMainBullets: 5,
            }}
            spaceBetween={20}
            slidesPerGroup={1}
            loop={communitiesData?.length > 4}
            breakpoints={{
              300: {
                slidesPerView: 1,
                spaceBetween: 16,
              },
              640: {
                slidesPerView: 2,
                spaceBetween: 18,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 20,
              },
              1280: {
                slidesPerView: 4,
                spaceBetween: 20,
              },
            }}
            navigation={true}
            modules={[Pagination, Navigation]}
            className="communities-slider !pb-12"
          >
            {communitiesData?.map((community) => (
              <SwiperSlide key={community._id} className="!h-auto pb-4">
                <CommunityCard data={community} isSlide={true} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      ) : (
        <div className="w-full min-h-[400px] flex flex-col justify-center items-center rounded-3xl bg-white/5 backdrop-blur-md border border-white/10 p-12">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary-green/20 flex items-center justify-center mb-6">
            <HiUserGroup className="w-10 h-10 text-white/40" />
          </div>
          <h3 className="text-2xl font-semibold text-white mb-2">No Communities Yet</h3>
          <p className="text-white/60 text-center max-w-md mb-6">
            Be the first to create a community and build a loyal following for your research organization.
          </p>
          {canCreateCommunity ? (
            <Link
              to="/create-community"
              className="px-6 py-3 bg-gradient-to-r from-primary to-primary-green text-white font-medium rounded-xl hover:opacity-90 transition-opacity"
            >
              Create Community
            </Link>
          ) : isLoggedIn && !hasCompletedProfile ? (
            <Link
              to="/create-profile"
              className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium rounded-xl hover:opacity-90 transition-opacity"
            >
              Complete Profile to Create
            </Link>
          ) : (
            <Link
              to="/communities"
              className="px-6 py-3 bg-gradient-to-r from-primary to-primary-green text-white font-medium rounded-xl hover:opacity-90 transition-opacity"
            >
              Explore Communities
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default HomeCommunities;
